import { Link } from 'react-router-dom';

export function TermsAndConditions() {
  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link 
          to="/"
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors"
        >
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Terms and Conditions
        </h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
          <p className="text-sm text-gray-400">Last updated: December 12, 2025</p>
          
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Agreement to Terms</h2>
            <p>
              By accessing or using instadrop.io (the "Service"), you agree to be bound by these Terms and Conditions
              ("Terms"). If you disagree with any part of these terms, you may not access the Service. These Terms apply 
              to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Description of Service</h2>
            <p>
              instadrop.io is a peer-to-peer file sharing platform that enables users to transfer files directly between
              devices using WebRTC technology. The Service facilitates direct connections between users without storing file 
              contents on our servers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">User Accounts</h2>
            <p>
              When you create an account with us, you must provide accurate, complete, and current information. Failure to 
              do so constitutes a breach of the Terms, which may result in immediate termination of your account.
            </p>
            <p className="mt-4">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining the confidentiality of your account and password</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Share, transmit, or store any illegal, harmful, or infringing content</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the intellectual property rights of others</li>
              <li>Distribute malware, viruses, or other harmful code</li>
              <li>Share content that is defamatory, obscene, or harassing</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service for any commercial purpose without our prior written consent</li>
              <li>Impersonate any person or entity</li>
              <li>Spam or send unsolicited communications through the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Content Responsibility</h2>
            <p>
              You retain all rights to any content you transfer through the Service. By using the Service, you represent and 
              warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You own or have the necessary rights to share the content</li>
              <li>Your content does not violate any applicable laws or third-party rights</li>
              <li>You are solely responsible for the content you share</li>
            </ul>
            <p className="mt-4">
              <strong>Important:</strong> Because we use peer-to-peer technology, we do not have access to, control over, 
              or store the actual files you transfer. You are entirely responsible for the content you share and receive.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Subscription and Payment</h2>
            <p>
              Certain features of the Service may require a paid subscription. By subscribing, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Pay all applicable subscription fees</li>
              <li>Automatic renewal unless you cancel before the renewal date</li>
              <li>Provide accurate payment information</li>
            </ul>
            <p className="mt-4">
              We reserve the right to change subscription prices with reasonable notice. Refunds are handled in accordance 
              with our refund policy and applicable consumer protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Service Availability</h2>
            <p>
              We strive to provide reliable service but do not guarantee that the Service will be:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Available at all times without interruption</li>
              <li>Secure or free from errors, bugs, or viruses</li>
              <li>Compatible with all devices or configurations</li>
            </ul>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time with 
              or without notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by instadrop.io and are
              protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="mt-4">
              You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Copy, modify, or create derivative works of the Service</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Remove any copyright or proprietary notices</li>
              <li>Use our trademarks without prior written permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, instadrop.io and its affiliates, officers, employees, agents,
              and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
              including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Loss of profits, data, or use</li>
              <li>Damage from viruses or malware transmitted through the Service</li>
              <li>Costs of procurement of substitute services</li>
              <li>Unauthorized access to or alteration of your transmissions or data</li>
            </ul>
            <p className="mt-4">
              In no event shall our total liability exceed the amount you paid us in the twelve (12) months preceding 
              the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
              NON-INFRINGEMENT.
            </p>
            <p className="mt-4">
              We do not warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The Service will meet your specific requirements</li>
              <li>File transfers will be secure, timely, uninterrupted, or error-free</li>
              <li>Any errors will be corrected</li>
              <li>The Service is free from viruses or harmful components</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless instadrop.io and its affiliates from any claims,
              damages, losses, liabilities, and expenses (including attorneys' fees) arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Content you share through the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice or 
              liability, for any reason, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Breach of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>At your request</li>
            </ul>
            <p className="mt-4">
              Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which 
              by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, 
              and limitations of liability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which 
              instadrop.io operates, without regard to its conflict of law provisions. Any disputes arising from these
              Terms or your use of the Service shall be resolved in the courts of that jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is 
              material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes 
              a material change will be determined at our sole discretion.
            </p>
            <p className="mt-4">
              By continuing to access or use our Service after revisions become effective, you agree to be bound by the 
              revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain 
              in full force and effect. The invalid or unenforceable provision will be deemed superseded by a valid provision 
              that most closely matches the intent of the original provision.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Entire Agreement</h2>
            <p>
              These Terms constitute the entire agreement between you and instadrop.io regarding the Service and supersede
              all prior and contemporaneous agreements, proposals, or representations, written or oral, concerning the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mt-4">
              Email: <a href="mailto:hi@instadrop.io" className="text-blue-400 hover:text-blue-300">legal@instadrop.io</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
